import React, { useState } from 'react';
import {
  SOURCE_STATE_CLASSES,
  buildLedgerContractExplanation,
  formatBookScopePostureLabel,
  formatBookScopePostureTooltip,
  formatLedgerBookSettingsTooltip,
  formatMoneyProvenanceTooltip,
  hasBookScopePosture,
  hasLedgerBookSettings,
  sanitizeStatementEntity,
  sanitizeStatementSubtitle,
} from '../source-state.js';
import { getLedgerSourceState } from '../data-ledger.js';
import ProvenanceChip from './ProvenanceChip.jsx';

/* v8 Path X — shared report-name header block for every multi-period
   table in the app. Previously only `FormalStatementTable` had this
   (entity + title + collapse toggle + period line); `MetricHorizonTable`
   had a bare replica. Both now consume this single component so the
   filing-style report caption reads identically on Formal Statements
   AND Comparative Tables.

   Props:
     entity     — private/operator-safe label
     title      — "Statement of Income — Accrual Basis"
     subtitle   — source-state-aware statement note
     period     — "2025-05 – 2026-04"
     collapsible — enables the ▾/▸ toggle (default true)
     defaultCollapsed — starting state (default false)

   Stays OUTSIDE the scroll container so it doesn't fight the sticky
   thead for space. Marked `z-[40]` so it sits above any content that
   scrolls under it. When a future Path adds sticky-report-name behaviour,
   it can be swapped in here without touching the two consumers. */
function ContractSignalStrip({ title, sourceState, compact = false }) {
  const explanation = buildLedgerContractExplanation({
    sourceState: sourceState || getLedgerSourceState(),
    title,
  });
  const state = explanation.sourceState;
  const chips = [];

  if (explanation.hasContractSignal) {
    chips.push({
      key: 'contract',
      kind: {
        sourceClass: SOURCE_STATE_CLASSES.CONTRACT_BACKED,
        label: 'Ledger contract',
        compactLabel: 'contract',
      },
      tooltip: 'Ledger contract fields are loaded into Lens source state; strict-live, money-rate provider, and public/client claims remain separate.',
    });
  } else {
    chips.push({
      key: 'source',
      kind: state,
      tooltip: state.tooltip,
    });
  }

  if (explanation.dataTierClass) {
    chips.push({
      key: 'tier',
      kind: {
        sourceClass: explanation.dataTierClass,
        label: explanation.dataTier || 'Source tier',
        compactLabel: explanation.dataTier || 'tier',
      },
      tooltip: `Ledger contract data tier: ${explanation.dataTier}. This is not upgraded locally by Lens.`,
    });
  }

  if (hasLedgerBookSettings(state)) {
    chips.push({
      key: 'book-settings',
      kind: {
        sourceClass: SOURCE_STATE_CLASSES.CONTRACT_BACKED,
        label: 'Ledger Book settings',
        compactLabel: 'book-settings',
      },
      tooltip: formatLedgerBookSettingsTooltip(state),
    });
  }

  if (hasBookScopePosture(state)) {
    const scopeStrict = state.canClaimStrictRealBook === true;
    const scopeLabel = formatBookScopePostureLabel(state, { compact: true });
    chips.push({
      key: 'book-scope',
      kind: {
        sourceClass: scopeStrict ? SOURCE_STATE_CLASSES.CONTRACT_BACKED : SOURCE_STATE_CLASSES.MISSING_WARNING,
        label: scopeStrict ? `Strict Book scope · ${scopeLabel}` : `Book scope · ${scopeLabel}`,
        compactLabel: `book-scope · ${scopeLabel}`,
      },
      tooltip: formatBookScopePostureTooltip(state),
    });
  }

  if (explanation.reportClass) {
    const reportLabel = explanation.authorshipReport?.label || explanation.reportType || 'Report';
    const strictRows = explanation.canClaimStrictReportRows === true;
    const aggregateRows = explanation.aggregateRowsAvailable === true;
    const postedAtoms = explanation.reportEmissionScope === 'posted-journal-line-atoms';
    const authoredRowsHeld = explanation.authoredByLedger && !strictRows;
    const label = aggregateRows
      ? strictRows
        ? 'Strict Ledger aggregate rows'
        : authoredRowsHeld
          ? 'Ledger aggregate rows held'
          : 'Ledger aggregate rows'
      : strictRows
        ? 'Strict Ledger rows'
        : authoredRowsHeld
          ? 'Ledger rows held'
          : 'Report rows not authored';
    const compactLabel = aggregateRows
      ? strictRows
        ? 'strict-aggregate'
        : authoredRowsHeld
          ? 'aggregate-held'
          : 'aggregate-rows'
      : strictRows
        ? 'strict-rows'
        : authoredRowsHeld
          ? 'rows-held'
          : 'not-authored';
    const tooltip = aggregateRows
      ? strictRows
        ? `${reportLabel}: Ledger emits strict service-derived aggregate rows from posted journal lines. Formal statement line items are not fully emitted in ledger.json v1.`
        : `${reportLabel}: Ledger aggregate rows are available, but strict proof is held. ${explanation.strictReportHeldReason || explanation.formalStatementLineItemHeldReason || 'Full strict Book/source posture is not available.'}`
      : postedAtoms && strictRows
        ? `${reportLabel}: reportLineAuthorship says rows are strict Ledger-authored posted journal-line atoms.`
        : authoredRowsHeld
          ? `${reportLabel}: Ledger-authored rows exist, but strict row proof is held. ${explanation.strictReportHeldReason || 'Full strict Book/source posture is not available.'}`
          : `${reportLabel}: reportLineAuthorship holds strict row authorship. ${explanation.strictReportHeldReason || 'No strict Ledger line item emitted for this report.'}`;
    chips.push({
      key: 'report-authorship',
      kind: {
        sourceClass: strictRows
          ? SOURCE_STATE_CLASSES.CONTRACT_BACKED
          : authoredRowsHeld
            ? SOURCE_STATE_CLASSES.MISSING_WARNING
            : SOURCE_STATE_CLASSES.NON_AUTHORED,
        label,
        compactLabel,
      },
      tooltip,
    });
  }

  if (explanation.moneyProvenance) {
    chips.push({
      key: 'money-provenance',
      kind: {
        sourceClass: explanation.moneyClass,
        label: 'Money provenance',
        compactLabel: 'money',
      },
      tooltip: formatMoneyProvenanceTooltip(explanation.moneyProvenance),
    });
  }

  const providerHealthWasChecked = Boolean(
    explanation.providerHealth?.checkedAt ||
    explanation.providerHoldReason
  );

  if (explanation.providerLiveProof) {
    chips.push({
      key: 'provider',
      kind: {
        sourceClass: SOURCE_STATE_CLASSES.CONTRACT_BACKED,
        label: 'Money-rate provider live',
        compactLabel: 'provider-live',
      },
      tooltip: `Money-rate provider proof is recent via ${explanation.providerHealth?.provider || 'provider-health route'} (${explanation.providerHealth?.samplePair || 'sample pair'}). Fiat FX and crypto pricing classes remain separate; /api/ledger.json remains non-calling provenance.`,
    });
  } else if (explanation.providerLiveNotTested) {
    chips.push({
      key: 'provider',
      kind: providerHealthWasChecked
        ? {
            sourceClass: SOURCE_STATE_CLASSES.MISSING_WARNING,
            label: 'Money-rate provider held',
            compactLabel: 'provider-held',
          }
        : SOURCE_STATE_CLASSES.PROVIDER_LIVE_NOT_TESTED,
      tooltip: explanation.providerHoldReason
        ? `Money-rate provider proof remains held: ${explanation.providerHoldReason}. /api/ledger.json remains non-calling provenance.`
        : 'Money-rate live provider is explicitly not tested by ledger.json; provider-live proof remains held.',
    });
  }

  if (explanation.warningCodes.length > 0) {
    const visibleWarnings = explanation.warningCodes.slice(0, 5);
    chips.push({
      key: 'warnings',
      kind: {
        sourceClass: SOURCE_STATE_CLASSES.MISSING_WARNING,
        label: 'Contract warnings',
        compactLabel: `${explanation.warningCodes.length} warning${explanation.warningCodes.length === 1 ? '' : 's'}`,
      },
      tooltip: `Contract warnings: ${visibleWarnings.join(', ')}${explanation.warningCodes.length > visibleWarnings.length ? ', ...' : ''}.`,
    });
  }

  return (
    <div className={`${compact ? 'mt-[4px]' : 'mt-[6px]'} flex items-center justify-center gap-[4px] flex-wrap`}>
      {chips.map((chip) => (
        <ProvenanceChip key={chip.key} kind={chip.kind} tooltip={chip.tooltip} />
      ))}
    </div>
  );
}

export default function StatementHeader({
  entity,
  title,
  subtitle,
  period,
  sourceState,
  collapsible = true,
  defaultCollapsed = false,
}) {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  const safeEntity = sanitizeStatementEntity(entity);
  const safeSubtitle = sanitizeStatementSubtitle(subtitle);
  const openCls    = 'px-5 pt-4 pb-3';
  const compactCls = 'px-5 pt-[6px] pb-1';
  const titleOpen    = 'text-[17px] font-bold mt-1';
  const titleCompact = 'text-[13px] font-bold mt-0';

  return (
    <header
      className={`text-center border-b border-border transition-[padding,font-size] duration-150 bg-[#1c1c30] z-[40] ${collapsed ? compactCls : openCls}`}
    >
      {!collapsed && safeEntity && <div className="text-[13px] text-text-muted">{safeEntity}</div>}
      {title && (
        <div className={`${collapsed ? titleCompact : titleOpen} text-text`}>
          {title}
          {collapsed && period ? ` · ${period}` : ''}
          {collapsible && (
            <button
              type="button"
              className="bg-transparent border-0 text-text-muted cursor-pointer text-[11px] pl-[6px] opacity-50 transition-opacity duration-150 hover:opacity-100 align-middle"
              onClick={() => setCollapsed(c => !c)}
              title={collapsed ? 'Expand header' : 'Collapse header'}
              aria-label={collapsed ? 'Expand header' : 'Collapse header'}
            >
              {collapsed ? '▸' : '▾'}
            </button>
          )}
        </div>
      )}
      {!collapsed && safeSubtitle && <div className="text-[12px] text-text-muted mt-[2px]">{safeSubtitle}</div>}
      {title && <ContractSignalStrip title={title} sourceState={sourceState} compact={collapsed} />}
      {!collapsed && period && <div className="text-[12px] text-text-muted mt-[2px]">{period}</div>}
    </header>
  );
}
